import React, { useEffect, useState, useRef, useContext } from "react";
import { useMsal } from "@azure/msal-react";
import { db } from "../../firebaseConfig";
import {
  doc,
  getDoc,
  writeBatch,
  collection,
  updateDoc,
  onSnapshot,
} from "firebase/firestore";
import { FaChevronLeft, FaChevronRight } from "react-icons/fa";
import "./Home.css";
import NavBar from "../Navbar";
import { Navigate } from "react-router-dom";
import Loading from "../Loading";
import { AuthContext } from "../AuthContext";
import SubsidiarySelectorModal from "./SubsidiarySelectorModal ";
import mixpanel from "../../utils/mixpanel";
import {
  getModulesForUserSubsidiaries,
  syncUserModules,
} from "../../utils/moduleUtils";
import {
  extractDomainFromEmail,
  getUserDocument,
} from "../../utils/userHelpers";

function Home() {
  const { accounts } = useMsal();
  const [modulesData, setModulesData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArrows, setShowArrows] = useState(false);
  const carouselRef = useRef(null);
  const [userInformations, setUserInformations] = useState();
  const [active, setActive] = useState(null);
  const { authorized, userEmailFromLogin } = useContext(AuthContext);
  const [subsidiarySelection, setSubsidiarySelection] = useState(null); // Subsidiária selecionada
  const [showSubsidiarySelector, setShowSubsidiarySelector] = useState(false);
  const [subsidiaries, setSubsidiaries] = useState([]); // Subsidiárias disponíveis
  const [refreshTrigger, setRefreshTrigger] = useState(0); // Trigger para forçar re-fetch
  const syncingRef = useRef(false); // Flag para evitar sync concorrente
  const lastProcessedEmailRef = useRef(null); // Cache do último email processado

  const sortedModules = [...modulesData].sort(
    (a, b) => a.priority - b.priority
  );

  // Mixpanel Module Tracking
  const trackModuleUsage = (moduleName) => {
    mixpanel.track(`[Core] Click in ${moduleName} Module`, { Module: "Core" });
  };

  const customStyles = {
    navbar: {
      backgroundColor: "white",
    },
    navbarText: {
      color: "black",
    },
    userInitialNavbar: {
      backgroundColor: "#004981",
    },
    userEllipse: {
      backgroundColor: "#0067FF",
    },
    userRectangle: {
      backgroundColor: "#0067FF",
    },
    arrowIcon: {
      color: "white",
    },
    dropdownMenu: {
      backgroundColor: "#0067FF",
    },
    dropdownItem: {
      color: "white",
    },
    backgroundRectangleNavbar: {
      backgroundColor: "#0067FF",
    },
    dropdownDivider: {
      backgroundColor: "white",
    },
    moduleEllipse: {
      backgroundColor: "#0067FF",
    },
    carouselInner: {
      justifyContent: "flex-start",
    },
  };

  const email =
    accounts.length > 0
      ? accounts[0].idTokenClaims?.email || accounts[0]?.username
      : authorized
      ? userEmailFromLogin
      : null;

  useEffect(() => {
    const fetchModulesData = async () => {
      // Verifica se já processou este email nesta execução E se não mudou subsidiarySelection
      const cacheKey = `${email}-${subsidiarySelection}`;
      if (lastProcessedEmailRef.current === cacheKey && email) {
        return;
      }

      if (!email) {
        console.warn("⚠️ [useEffect] Email não encontrado, abortando busca");
        setLoading(false);
        return;
      }

      // Marca como processado
      lastProcessedEmailRef.current = cacheKey;

      try {
        const domain = extractDomainFromEmail(email);
        // console.log('[fetchModulesData] Domínio do email:', domain);
        if (!domain) {
          // console.warn('[fetchModulesData] Domínio não encontrado no email');
          setLoading(false);
          return;
        }

        const userDocResult = await getUserDocument(email);
        let userDoc = userDocResult?.userData || null;
        let actualDocumentRef = userDocResult?.docRef || null;

        const corporateDomainRef = doc(db, "regional_enterprises", domain);
        const corporateDomainDoc = await getDoc(corporateDomainRef);

        if (userDoc) {
          setUserInformations(userDoc);
          setActive(userDoc.isActive ?? false);

          // 🔄 SYNC: Garante que módulos estão atualizados (APENAS UMA VEZ por sessão)
          const syncKey = `module_sync_${email}`;
          const hasSyncedThisSession =
            sessionStorage.getItem(syncKey) === "true";

          // Carrega módulos
          const loadModules = async (userData) => {
            const modules = userData.accessibleModules || [];
            const modulePromises = modules.map((ref) =>
              getDoc(ref).then((doc) => doc.data())
            );
            const modulesData = await Promise.all(modulePromises);
            setModulesData(modulesData.filter((module) => module.isActive));
          };

          if (!hasSyncedThisSession && !syncingRef.current) {
            syncingRef.current = true; // Marca que está sincronizando

            try {
              const wasSynced = await syncUserModules(
                email,
                domain,
                userDoc.subsidiaries || []
              );
              sessionStorage.setItem(syncKey, "true"); // Marca sync como completo para esta sessão

              if (wasSynced) {
                // Recarrega o usuário atualizado do documento correto
                const updatedUserDoc = await getDoc(actualDocumentRef);
                const updatedUsersMap = updatedUserDoc.data().users;
                const updatedUser = updatedUsersMap[email];
                setUserInformations(updatedUser);
                await loadModules(updatedUser);
              } else {
                await loadModules(userDoc);
              }
            } finally {
              syncingRef.current = false; // Libera flag
            }
          } else if (syncingRef.current) {
            await loadModules(userDoc);
          } else {
            await loadModules(userDoc);
          }
        }

        if (!userDoc && corporateDomainDoc.exists()) {
          // Obtém subsidiárias do documento corporativo (usa dados locais, não state)
          const domainSubsidiaries = corporateDomainDoc.data().subsidiary || [];

          if (domainSubsidiaries.length > 1 && !subsidiarySelection) {
            setActive(true);
            setSubsidiaries(domainSubsidiaries);
            setShowSubsidiarySelector(true);
            return; // Aguarda a seleção antes de continuar
          }
          setActive(true);
          // Determina as subsidiárias do novo usuário
          const selectedSubsidiaries =
            subsidiarySelection && subsidiarySelection.length > 0
              ? subsidiarySelection
              : domainSubsidiaries;

          // Obtém TODOS os módulos acessíveis baseado nas subsidiárias (públicos + específicos)
          const accessibleModules = await getModulesForUserSubsidiaries(
            selectedSubsidiaries
          );

          const nameComplete = email.split("@")[0];
          const name = nameComplete
            .split(".")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
          const role = "user";
          const isActive = true;

          const newUser = {
            name,
            email,
            role,
            isActive,
            accessibleModules: accessibleModules || [],
            subsidiaries: selectedSubsidiaries,
          };

          const batch = writeBatch(db);
          batch.set(
            corporateDomainRef,
            {
              users: { [email]: newUser },
            },
            { merge: true }
          );

          await batch.commit();

          // Define estados locais antes de fechar o modal
          setUserInformations(newUser);
          setActive(true);

          // Carrega os módulos
          const modulePromises = accessibleModules.map((ref) =>
            getDoc(ref).then((doc) => doc.data())
          );
          const modulesDataArray = await Promise.all(modulePromises);
          setModulesData(modulesDataArray.filter((module) => module.isActive));

          // Tracking de novo usuário
          mixpanel.track("[Core] First Login/New Account", {
            "Login Method": "Microsoft",
            Module: "Core",
          });

          setShowSubsidiarySelector(false); // Fecha o modal
          setLoading(false); // Finaliza loading
        }
      } catch (error) {
        console.warn("[fetchModulesData] Erro ao buscar dados:", error);
      } finally {
        // console.log('[fetchModulesData] subsidiarySelection: ', subsidiarySelection);
        // console.log('[fetchModulesData] Finalizado');
        setLoading(false);
      }
    };

    fetchModulesData();
  }, [
    email, // Usa length ao invés do array completo
    authorized,
    userEmailFromLogin,
    subsidiarySelection,
    refreshTrigger,
  ]);

  // 🔥 Listener em tempo real para mudanças nos módulos
  useEffect(() => {
    if (!email || !userInformations) {
      return; // Não cria listener se não tem usuário
    }

    const domain = extractDomainFromEmail(email);
    if (!domain) return;

    // Listener 1: Monitora mudanças na collection 'modules'
    const modulesUnsubscribe = onSnapshot(
      collection(db, "modules"),
      async (snapshot) => {
        if (!snapshot.metadata.hasPendingWrites) {
          const syncKey = `module_sync_${email}`;
          sessionStorage.removeItem(syncKey);
          lastProcessedEmailRef.current = null;

          // Força sync imediato quando módulos mudam
          const userData = userInformations; // Usa estado atual
          if (userData) {
            await syncUserModules(email, domain, userData.subsidiaries || []);
          }

          setRefreshTrigger((prev) => prev + 1);
        }
      }
      // ...
    );

    // Listener 2: Monitora mudanças no documento 'other_users' (para emails genéricos)
    // IMPORTANTE: Não faz comparação porque syncUserModules já garante que só atualiza quando necessário
    const otherUsersRef = doc(db, "regional_enterprises", "other_users");

    const otherUsersUnsubscribe = onSnapshot(
      otherUsersRef,
      (snapshot) => {
        if (!snapshot.metadata.hasPendingWrites && snapshot.exists()) {
          const usersMap = snapshot.data().users || {};
          const updatedUser = usersMap[email];

          if (updatedUser) {
            const syncKey = `module_sync_${email}`;
            sessionStorage.removeItem(syncKey);
            lastProcessedEmailRef.current = null;
            setUserInformations(updatedUser);
            setRefreshTrigger((prev) => prev + 1);
          }
        }
      },
      (error) => {
        console.error("[Realtime] Erro no listener de other_users:", error);
      }
    );

    // Listener 3: Monitora o domínio corporativo (se aplicável)
    let domainUnsubscribe = null;
    const userDomainRef = doc(db, "regional_enterprises", domain);

    getDoc(userDomainRef).then((userDomainDoc) => {
      if (userDomainDoc.exists() && userDomainDoc.data().users?.[email]) {
        domainUnsubscribe = onSnapshot(
          userDomainRef,
          (snapshot) => {
            if (!snapshot.metadata.hasPendingWrites && snapshot.exists()) {
              const usersMap = snapshot.data().users || {};
              const updatedUser = usersMap[email];

              if (updatedUser) {
                const syncKey = `module_sync_${email}`;
                sessionStorage.removeItem(syncKey);
                lastProcessedEmailRef.current = null;
                setUserInformations(updatedUser);
                setRefreshTrigger((prev) => prev + 1);
              }
            }
          },
          (error) => {
            console.error(`[Realtime] Erro no listener de ${domain}:`, error);
          }
        );
      }
    });

    // Cleanup: Remove listeners quando componente desmontar
    return () => {
      modulesUnsubscribe();
      otherUsersUnsubscribe();
      if (domainUnsubscribe) {
        domainUnsubscribe();
      }
    };
  }, [accounts, authorized, userEmailFromLogin, userInformations?.email]);

  useEffect(() => {
    const checkArrowsVisibility = () => {
      if (carouselRef.current) {
        const containerWidth = carouselRef.current.clientWidth;
        const scrollWidth = carouselRef.current.scrollWidth;
        setShowArrows(scrollWidth > containerWidth);
      }
    };

    checkArrowsVisibility();
    window.addEventListener("resize", checkArrowsVisibility);

    return () => {
      window.removeEventListener("resize", checkArrowsVisibility);
    };
  }, [modulesData]);

  const handleSubsidiarySelection = async (selectedSubsidiary) => {
    setSubsidiarySelection([selectedSubsidiary]);
    setShowSubsidiarySelector(false);
  };

  if (showSubsidiarySelector) {
    return (
      <SubsidiarySelectorModal
        subsidiaries={subsidiaries}
        onSelect={handleSubsidiarySelection}
      />
    );
  }

  const scrollCarousel = (direction) => {
    if (carouselRef.current) {
      const scrollAmount = 300; // Quantidade de rolagem
      const scrollDistance =
        direction === "left" ? -scrollAmount : scrollAmount;
      carouselRef.current.scrollLeft += scrollDistance;
    }
  };

  // Função para garantir que o link comece com http:// ou https://
  const ensureProtocol = (url) => {
    if (!/^https?:\/\//i.test(url)) {
      return `https://${url}`;
    }
    return url;
  };

  if (loading || !userInformations) {
    // console.log('[Home] Exibindo carregamento');
    return <Loading />;
  }

  if (!active) {
    // console.warn('[Home] Usuário inativo, redirecionando para página não autorizada');
    return <Navigate to="/unauthorized" />;
  }

  return (
    <div className="mainpage">
      <NavBar
        userInformations={userInformations}
        customStylesNavbar={customStyles}
        navbarText={"Hello, "}
        pageMode={"corporate"}
      />

      <div className="main-home">
        <div className="main-container-home">
          <div>
            <div className="text-container-homepage">
              <p className="text1">Have everything at your fingertips: </p>
              <p className="text2">tools, profiles, projects, and more.</p>
            </div>

            {/* Botão de rolagem para a esquerda */}
            {showArrows && (
              <button
                onClick={() => scrollCarousel("left")}
                style={{
                  position: "absolute",
                  left: "0",
                  zIndex: 2,
                  backgroundColor: "white",
                  border: "none",
                  cursor: "pointer",
                  padding: "10px",
                }}
              >
                <FaChevronLeft size={30} color="#5ABEFE" />
              </button>
            )}

            {/* Contêiner do carrossel */}
            <div className="carouselOuter">
              {/* Contêiner do carrossel */}
              <div
                ref={carouselRef}
                className="carouselInner"
                style={customStyles.carouselInner}
              >
                {modulesData.length > 0 && (
                  <div className="gridContainer">
                    {sortedModules.map((moduleData, index) => {
                      return (
                        <a
                          key={index}
                          href={ensureProtocol(moduleData.pageURL)}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="homepage-card"
                          onClick={() => trackModuleUsage(moduleData.pageName)}
                        >
                          <div className="icon-container">
                            <div
                              className="moduleEllipse"
                              style={customStyles.moduleEllipse}
                            >
                              {moduleData.pageIconUrl.startsWith("http") ? (
                                <img
                                  className="module-icon"
                                  src={`${moduleData.pageIconUrl}`}
                                  style={{
                                    width: "60%", // Ajusta a largura à elipse
                                    height: "60%", // Ajusta a altura à elipse
                                    objectFit: "contain", // Ajusta a proporção da imagem
                                  }}
                                  aria-label="Ícone do módulo" // Acessibilidade
                                ></img>
                              ) : (
                                <span style={{ color: "white", fontSize: 18 }}>
                                  No Icon
                                </span>
                              )}
                            </div>
                          </div>

                          {/* Nome do módulo */}
                          <div className="text-style-card-homepage">
                            {moduleData.pageName}
                          </div>

                          {/* Descrição do módulo */}
                          <div className="description-homepage">
                            {moduleData.description}
                          </div>
                        </a>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Botão de rolagem para a direita */}
            {showArrows && (
              <button
                onClick={() => scrollCarousel("right")}
                style={{
                  position: "absolute",
                  right: "0",
                  zIndex: 2,
                  backgroundColor: "white",
                  border: "none",
                  cursor: "pointer",
                  padding: "10px",
                }}
              >
                <FaChevronRight size={30} color="#5ABEFE" />
              </button>
            )}
          </div>
        </div>

        <span
          style={{
            marginLeft: "auto",
            padding: 5,
            paddingRight: 20,
            color: "#0067FF",
          }}
        >{`v${process.env.REACT_APP_VERSION}`}</span>
        <div className="orange-footer-homepage"></div>
      </div>
    </div>
  );
}

export default Home;
